import type {
  CoreAssistantMessage,
  CoreMessage,
  CoreToolMessage,
  Message,
  TextStreamPart,
  ToolInvocation,
  ToolSet,
} from 'ai';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { getFromCache, setInCache } from './cache';
import type { Message as DBMessage, Document } from '@/lib/db/schema';
import { extractTemplateContent, extractTemplateInitial } from './editor/extractTemplateContent';
import {type Doc, type Point, Node, type ExtractedInitial, type UIMessage, type UserType } from './type';

export const isEn:boolean = false;
export const LocalSelectModelFlag = 'localSelectedChatModel';
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isVIP = (userType?: UserType) => {
  return [ 'vip1' , 'vip2' , 'vip3' , 'vip4' , 'vip5', 'coperator' , 'admin' , 'system'].includes(userType || 'useType');
};

interface ApplicationError extends Error {
  info: string;
  status: number;
}

export const fetcher_backup = async (url: string) => {
  const res = await fetch(url);

  if (!res.ok) {
    const error = new Error(
      'An error occurred while fetching the data.',
    ) as ApplicationError;

    error.info = await res.json();
    error.status = res.status;

    throw error;
  }

  return res.json();
};


// 实现说明：
// 缓存存储结构：
// 使用Map对象存储缓存数据，键为请求URL，值为包含数据和时间戳的对象
// 独立存储正在进行的请求Promise，防止重复请求

// 缓存逻辑流程：
// 检查是否存在有效缓存 → 存在则直接返回
// 检查是否存在进行中的请求 → 存在则等待结果
// 创建新请求 → 存储Promise → 请求完成后更新缓存

// 并发请求处理：
// 使用promiseCache存储进行中的请求Promise
// 确保同一URL的多个并发请求共享同一个Promise

// 缓存有效期控制：
// 默认5分钟有效期（DEFAULT_MAX_AGE）
// 可通过第二个参数自定义有效期（单位：毫秒）

// 错误处理：
// 请求失败时会自动清理promiseCache
// 错误抛出机制保持原有逻辑不变

// 扩展功能：
// 提供clearCache方法用于手动清除缓存
// 支持清除单个URL或全部缓存

// 普通请求（使用默认5分钟缓存）
// const data = await fetcher('/api/data');
// 自定义缓存时间（10分钟）
// const data = await fetcher('/api/data', 10 * 60 * 1000);
// 清除特定URL缓存
// clearCache('/api/data');
// 清除所有缓存
// clearCache();


// 定义缓存项的结构
interface CacheItem {
  data: any;
  timestamp: number;
}

// 缓存存储对象和正在进行的请求Promise缓存
const cache = new Map<string, CacheItem>();
const promiseCache = new Map<string, Promise<any>>();

// 默认缓存有效时间为5分钟（可根据需求调整）
const DEFAULT_MAX_AGE = 10 * 60 * 1000;

export const fetcher = async (url: string,  fresh = false, maxAge: number = DEFAULT_MAX_AGE,) => {

  if (!fresh) {
    // 检查有效缓存是否存在
    const cachedItem = cache.get(url);
    /// sslog('cache.get url and item:', {url, cachedItem});
    if (cachedItem && Date.now() - cachedItem.timestamp < maxAge) {
      return cachedItem.data;
    }
  }

  // 检查是否有正在进行的请求
  const existingPromise = promiseCache.get(url);
  if (existingPromise) {
    return await existingPromise;
  }

  // 创建新的请求Promise
  const promise = (async () => {
    try {
      const res = await fetch(url);

      if (!res.ok) {
        const error = new Error(
          'An error occurred while fetching the data.'
        ) as ApplicationError;
        error.info = await res.json();
        error.status = res.status;
        throw error;
      }

      const data = await res.json();

      console.log('cache.set url and data:', url, data);

      // 更新缓存
      cache.set(url, {
        data,
        timestamp: Date.now()
      });

      return data;
    } finally {
      // 清理进行中的请求缓存
      promiseCache.delete(url);
    }
  })();

  // 存储当前请求的Promise
  promiseCache.set(url, promise);

  return await promise;
};

/**
 * 检查路径是否已经是本地化的
 * @param path - 路径字符串
 * @returns boolean
 */
const locales = ['en', 'zh'];
export function isPathLocalized(path: string): boolean {
  const pathSegment = path.split('/')[1] as 'en' | 'zh';
  return locales.includes(pathSegment);
}

/**
 * 为给定的路径添加当前的 locale 前缀
 * @param path - 原始路径
 * @param locale - 当前的 locale
 * @returns 本地化后的路径
 */
export function localizePath(path: string, locale: string): string {
  // 1. 如果路径已经是本地化的，直接返回
  if (isPathLocalized(path)) {
    return path;
  }

  // 2. 忽略绝对 URL 和锚点链接
  if (path.startsWith('http') || path.startsWith('//') || path.startsWith('#')) {
    return path;
  }

  // 3. 添加 locale 前缀
  // 确保路径以 / 开头
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `/${locale}${normalizedPath}`;
}


// 可选：提供手动清除缓存的方法
export const clearCache = (url?: string) => {
  if (url) {
    cache.delete(url);
    promiseCache.delete(url);
  } else {
    cache.clear();
    promiseCache.clear();
  }
};


// const BaseCodings = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+@-/.';
const BaseCodings = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ.@+／-:[]_';
export function decode(input: string): string {
  if (!input) return '';
  
  const array: string[] = [];
  let i = 0;
  
  while (i < input.length) {
    const char = input[i];
    const lengthChar = input[i + 1];
    
    if (!lengthChar) break; // 防止越界
    
    const charLen = BaseCodings.indexOf(lengthChar);
    if (charLen === -1) {
      i++;
      continue; // 跳过无效长度字符
    }
    
    const start = i + 2;
    const end = start + charLen;
    
    if (end > input.length) break; // 防止越界
    
    for (let j = start; j < end; j++) {
      const posChar = input[j];
      const position = BaseCodings.indexOf(posChar);
      
      if (position !== -1) {
        array[position] = char;
      }
    }
    
    i = end;
  }
  
  return array.join('');
}

export function encode(input: string | null | undefined): string {
  if (!input) return '';
  
  // 处理超长输入的情况
  if (input.length >= BaseCodings.length) {
    return input.substring(0, BaseCodings.length - 1);
  }
  
  const ruleDict: Record<string, string> = {};
  
  // 构建字符位置映射
  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    const codingChar = BaseCodings[i];
    
    if (ruleDict[char]) {
      ruleDict[char] += codingChar;
    } else {
      ruleDict[char] = codingChar;
    }
  }
  
  let coding = '';
  
  // 生成编码字符串
  for (const [char, positions] of Object.entries(ruleDict)) {
    const lengthCode = BaseCodings[positions.length];
    
    if (!lengthCode) {
      // 如果位置长度超出编码范围则跳过
      continue;
    }
    
    coding += `${char}${lengthCode}${positions}`;
  }
  
  return coding;
}

// 定义提取函数
export function extractTextPartsWithLastChar(input: string): string[] {
  // 从后面查找最后一个中文冒号的位置
  const lastColonIndex = input.lastIndexOf('：');
  
  if (lastColonIndex === -1) {
    // 如果没有找到中文冒号，从后面查找最后一个英文冒号
    const lastEnglishColonIndex = input.lastIndexOf(':');
    if (lastEnglishColonIndex === -1) {
      // 如果都没有冒号，返回整个字符串作为emojiPart，原串作为descriptionPart
      return [input, input]
    }
    
    return [
      input.substring(0, lastEnglishColonIndex),
      input.substring(lastEnglishColonIndex + 1)
    ];
  }
  
  return [
    input.substring(0, lastColonIndex),
    input.substring(lastColonIndex + 1)
  ];
}


// 定义函数, 检查字符串是否符合特定格式（即字符串必须包含 # 和 @，并且被分成三个部分），并将其分割为一个数组，可以按照以下步骤进行：
export function checkAndSplitString(input: string): [string, string, string] | null {
  // 正则表达式，确保字符串包含 # 和 @，并且被分成三个部分
  const pattern = /^(.+?)@(.+?).(.+)$/;

  // 测试字符串是否符合格式
  if (pattern.test(input)) {

    console.log('pattern checkAndSplitString:', input);

    // 分割字符串
    const parts = input.split(/@|\./); // . 是正则表达式中的通配符（匹配任意字符），需用 \. 表示字面量的 .。
    console.log('parts checkAndSplitString:', parts);

    // 检查每个子字符串是否符合要求（不以数字开头）
    const isValid = parts.every((part) => /^[^0-9]/.test(part));

    if (parts.length === 3 && isValid) {
      return [parts[0], parts[1], parts[2]];
    }
  }

  // 如果不符合格式，返回 null
  return null;
}

function convertToNumbers(parts: string[]): number[] {
  return parts.map(part => {
      const num = Number(part);
      if (Number.isNaN(num)) {
          throw new Error(`无法转换为数字: ${part}`);
      }
      return num;
  });
}

// 定义函数, 检查字符串是否符合特定格式（即字符串必须包含 S 和 G，并且被分成三个部分），并将其分割为一个数组，可以按照以下步骤进行：
export function checkAndSplitNumberString(input: string): [number, number, number] {
  // 正则表达式，确保字符串包含 # 和 @，并且被分成三个部分
  const pattern = /^(.+?)S(.+?)G(.+)$/;

  // 测试字符串是否符合格式
  if (pattern.test(input)) {
    // 分割字符串
    const parts = input.split(/S|G/);

    // 检查每个子字符串是否符合要求（全都是数字）这里的 \d+ 表示匹配一个或多个数字字符，而 ^ 和 $ 分别表示字符串的开始和结束，确保整个部分完全由数字组成。   
    const isValid = parts.every((part) => /^\d+$/.test(part));

    if (parts.length === 3 && isValid) {

      // 转换为 number 类型
      try {
        // const numbers: number[] = parts.map(part => Number(part));
        const numbers = convertToNumbers(parts);
        return [numbers[0], numbers[1], numbers[2]];
      } catch (error) {
          console.error(error);
      }
    } else {
      return [0,0,0];
    }
  }

  // 如果不符合格式，返回 null
  return [0, 3, 10];
}


export const extractArray = (str: string): string[] => {
  // 使用正则匹配方括号内的内容（非贪婪模式匹配第一个出现的括号）
  const match = str.match(/^\[(.*?)\]/);
  if (!match) return [];
  // 将匹配到的内容按逗号分割，并去除每个元素的首尾空格
  return match[1].split(',').map(item => item.trim());
};

/**
 * 从字符串中提取开头的数组并返回剩余部分
 * @param str 输入字符串
 * @returns 包含提取的数组和剩余字符串的对象
 */
function extractArrayAndremaining(str: string): { array: string[], remaining: string } {
  // 使用正则匹配方括号内的内容（非贪婪模式匹配第一个出现的括号）
  const match = str.match(/^\[(.*?)\]/);
  
  if (!match) {
    return { array: [], remaining: str };
  }
  
  // 提取匹配的数组部分
  const arrayContent = match[1];
  const array = arrayContent.split(',').map(item => item.trim());
  
  // 获取剩余字符串
  const remaining = str.substring(match[0].length);
  
  return { array, remaining };
}

const extractAfterAt = (str: string): string => {
  return str.split('@')[1] || '';
};

export const extractAfterLastAt = (str: string): string => {
  const lastAtIndex = str.lastIndexOf('@');
  return lastAtIndex === -1 ? str : str.substring(lastAtIndex + 1);
};

// 将一个字符串根据某个字符号裁剪得到前半部分，并将前半部分转为数字类型，后半部分还是返回字符串类型
// 如果分隔符位于字符串的最前面，且后半部分的字符串很长，使用 split() 方法可能会导致性能问题，因为 split() 会将整个字符串分割成多个部分，即使我们只需要前两部分。在这种情况下，使用 split() 方法可能会产生不必要的性能开销，尤其是当字符串非常长时。
export function splitAndConvert(input: string, delimiter: string): [string[], string, string[], string] {

  if (!input || input.trim().length === 0) {
    console.error('limk.log.error - delimiter and input:', delimiter, input);
    return [[], '0S0G0',[], input];
  }

  // 找到分隔符的位置
  const index = input.indexOf(delimiter);

  // 如果分隔符不存在，抛出错误
  if (index === -1) {
    console.error('limk.log.error - delimiter and input:', delimiter, input);
    throw new Error('Input string does not contain the delimiter.');
  }

  // 获取前半部分并转换为数字
  // const firstPart = parseFloat(input.substring(0, index));
  // 检查前半部分是否为有效数字
  // if (isNaN(firstPart)) {
  //   throw new Error('The first part of the input string is not a valid number.');
  // }

  const firstPart = input.substring(0, index);
  // 获取后半部分
  const secondPart = input.substring(index + delimiter.length);

  // const firstArray = extractArray(firstPart);

  const {array:firstArray, remaining:first} = extractArrayAndremaining(firstPart);
  const {array:secondArray, remaining:second} = extractArrayAndremaining(secondPart);

  return [firstArray, first, secondArray, second];
}

// 将数组按照每组 n 个元素的方式进行分组。
// 代码说明
// 函数签名：
// array: T[]：输入数组，类型为泛型 T，以支持任意类型的数组。
// chunkSize: number：每组的大小。
// 错误处理：
// 如果 chunkSize 小于或等于 0，抛出错误。
// 分组逻辑：
// 使用 for 循环，每次迭代跳过 chunkSize 个元素。
// 使用 array.slice(i, i + chunkSize) 提取当前组的子数组。
// 将子数组推入结果数组 result。
// 返回值：
// 返回一个二维数组，其中每个子数组包含最多 chunkSize 个元素。
// 示例用法
// const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
// const chunkSize = 3;
// const groupedNumbers = chunkArray(numbers, chunkSize);
// console.log(groupedNumbers);
// 输出: [[1, 2, 3], [4, 5, 6], [7, 8, 9]]

export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  if (chunkSize <= 0) {
    throw new Error('Chunk size must be greater than 0');
  }

  const result: T[][] = [];

  if (array.length < chunkSize) {
    result.push(array)
  } else {
    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize));
    }
  }
  return result;
}

export function safeSubstring(str: string, start: number, end?: number): string {
  if (end !== undefined && end > str.length) {
    // 如果指定的结束索引超出字符串长度，则调整为字符串的实际长度
    return str.substring(start, str.length);
  }
  return str;
}

export function extractBasePath(path: string) {
  // 分割路径，得到各个部分
  const parts = path.split('/');
    
  // 去除空部分
  const filteredParts = parts.filter(part => part !== '');

  // 如果路径为空或只有一个斜杠，返回根路径
  if (filteredParts.length === 0) {
    return '/';
  }

  // 构建基础路径
  const basePath = `/${filteredParts[0]}`;

  // 如果路径只有基础部分，返回基础路径
  if (filteredParts.length === 1) {
    return basePath;
  }

  // 如果路径有更多部分，返回基础路径加上一个斜杠
  return `${basePath}/`;
}


// types.ts (或者直接放在组件/工具文件中)
export interface FileBlock {
  title: string;
  code: string;
  language?: string;
}

/**
 * 解析被 ~~~代码文件组 ... ~~~ 包裹的多文件代码块
 * @param input 原始的大段文本
 * @returns 解析后的 FileBlock 数组
 */
export function parseCodeFileGroup(input: string): FileBlock[] {
  // 1. 首先提取 ~~~代码文件组 和 ~~~ 之间的内容
  // [\s\S]*? 表示非贪婪匹配所有字符（包括换行符）
  const groupWrapperRegex = /@codeblocks_begin\s*([\s\S]*?)@codeblocks_end/;
  const groupMatch = input.match(groupWrapperRegex);

  // 如果没有找到外层包裹，返回空数组 (或者根据需求决定是否解析全文)
  if (!groupMatch || !groupMatch[1]) {
    return [];
  }

  const innerContent = groupMatch[1];
  const blocks: FileBlock[] = [];

  // 2. 正则匹配内部的 ```文件名.后缀 ... ``` 结构
  // 解释：
  // ```        -> 匹配开头的三个反引号
  // ([^\n]+)   -> 捕获组1：文件名部分（直到换行符），例如 "短视频.prompt"
  // \n         -> 匹配文件名后的换行
  // ([\s\S]*?) -> 捕获组2：代码内容（非贪婪匹配所有字符）
  // ```        -> 匹配结尾的三个反引号
  const blockRegex = /```([^\n]+)\n([\s\S]*?)```/g;

  // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
  let match;
  // 使用 exec 循环匹配所有出现的代码块
  // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
    while ((match = blockRegex.exec(innerContent)) !== null) {
    const fullHeader = match[1].trim(); // 例如: "短视频.prompt"
    const rawContent = match[2];        // 代码原始内容

    // 3. 解析文件名和后缀
    // 逻辑：找到最后一个点号，前面是 title，后面是 language
    const lastDotIndex = fullHeader.lastIndexOf('.');
    
    let title = fullHeader;
    let language = 'ts'; // 默认语言

    if (lastDotIndex !== -1) {
      title = fullHeader.substring(0, lastDotIndex);
      language = fullHeader.substring(lastDotIndex + 1);
    } else {
      // 如果没有点号（例如 ```bash），则 title 为 bash，language 为 bash
      // 或者你可以根据需求设定 title = fullHeader, language = fullHeader
      language = fullHeader; 
    }

    blocks.push({
      title: title,
      code: rawContent.trim(), // 去除首尾多余空白，保留中间格式
      language: language
    });
  }

  return blocks;
}


//*************************** */


interface ContentItem {
  type: string;
  text: string;
}

export interface AssistantResponse {
  role: string;
  content: ContentItem[];
}

// 提取并解析JSON数据的函数
export function extractJSONFromResponse(response: AssistantResponse): any {
  try {
    // 找到第一个包含JSON的text内容
    const jsonContent = response.content.find(item => 
      item.type === 'text' && item.text.includes('维度参数')
    );

    if (!jsonContent) {
      throw new Error('No JSON content found');
    }

    if (jsonContent.text.includes('```json')) {
      // // 提取JSON部分 - 移除 ```json 和结尾的 ```
      // const jsonString = jsonContent.text
      //   .replace(/```json\n/, '')  // 移除开头的 ```json
      //   .replace(/\n```$/, '')     // 移除结尾的 ```
      //   .trim();

      // // 解析JSON
      // return JSON.parse(jsonString);

      // const jsonMatch = jsonContent.text.match(/```json\n([\s\S]*?)\n```/);
      const jsonMatch = jsonContent.text.match(/```json([\s\S]*?)```/);
      if (!jsonMatch) {
        throw new Error('No JSON found in text');
      }
      return JSON.parse(jsonMatch[1]);
    } else {
      // const jsonMatch = jsonContent.text.match(/{\n([\s\S]*?)\n}/);
      // if (!jsonMatch) {
      //   throw new Error('No JSON found in text');
      // }
      // return JSON.parse(jsonMatch[1]);


      // 1. 先取 { 与 } 之间的“裸”内容
      const innerMatch = jsonContent.text.match(/\{([\s\S]*?)\}/);
      if (!innerMatch) throw new Error('找不到 JSON 块');

      // const raw = innerMatch[1];          // 此时前后不带 { }

      const raw = jsonContent.text
        .replace(/^[^{]*\{/, '')   // 去掉 { 及其左侧所有字符
        .replace(/\}[^}]*$/, ''); // 去掉 } 及其右侧所有字符

      // const finalJsonStr = `{${raw}}`;
      return JSON.parse(`{${raw}}`);
    }
  } catch (error) {
    console.error('Error extracting JSON:', error);
    throw error;
  }
}

  // try {
  //   const extractedData = extractJSONFromResponse(response);
  //   console.log('提取的JSON数据:', extractedData);
  //   return extractedData;
  // } catch (error) {
  //   console.error('处理响应时出错:', error);
  //   return null;
  // }



// 更健壮的版本，包含类型定义
interface ExtractedData {
  任务主题: string[];
  维度参数: string[];
  还想知道: string[];
  关联延伸: string[];
}

export function extractJSONWithType(response: AssistantResponse): ExtractedData {
  const data = extractJSONFromResponse(response);
  
  // 类型验证
  if (
    typeof data === 'object' &&
    Array.isArray(data.任务主题) &&
    Array.isArray(data.维度参数) &&
    Array.isArray(data.还想知道) &&
    Array.isArray(data.关联延伸)
  ) {
    return data as ExtractedData;
  }
  
  throw new Error('提取的数据格式不正确');
}

// 简洁版本
export function extractJSONSimple(text: string): any {
  const jsonMatch = text.match(/```json\n([\s\S]*?)\n```/);
  if (!jsonMatch) {
    throw new Error('No JSON found in text');
  }
  return JSON.parse(jsonMatch[1]);
}

// 使用
// const jsonData = extractJSONSimple(response.content[0].text);



export function getCachedData(cacheKey:string, doc: Doc): { textBeforeHorizontalRule: string; codeBlockTexts: string[]; taskList: any[] } {
  //   const cacheKey = JSON.stringify(doc);
  
    let result = getFromCache(cacheKey);
    if (!result) {
      result = extractTemplateContent(doc);
      setInCache(cacheKey, result, 12 * 1000); // 缓存 12 秒
    }
  
    return result;
}

export function getCachedInitial(cacheKey:string, doc: Doc): ExtractedInitial {
  //   const cacheKey = JSON.stringify(doc);
  
    let result = getFromCache(cacheKey);
    if (!result) {
      result = extractTemplateInitial(doc);
      setInCache(cacheKey, result, 12 * 1000); // 缓存 12 秒
    }
  
    return result;
}


export function getLocalStorage(key: string) {
  if (typeof window !== 'undefined') {
    return JSON.parse(localStorage.getItem(key) || '[]');
  }
  return [];
}

export const setLocalStorage = (key: string, data: any) => {
  if (typeof window !== 'undefined') {
    console.log('setLocalStorage:', key, data);
    localStorage.setItem(key, JSON.stringify(data));
  }
};

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

import { generateId } from 'ai';
import { genSaltSync, hashSync } from 'bcrypt-ts';
import { sslog } from './utils/sslog';
// import md5 from 'crypto-js/md5';

export function generateHashedPassword(password: string) {
  const salt = genSaltSync(10);
  const hash = hashSync(password, salt);

  return hash;
}

export function generateDummyPassword() {
  const password = generateId(12);
  const hashedPassword = generateHashedPassword(password);

  return hashedPassword;
}


function addToolMessageToChat({
  toolMessage,
  messages,
}: {
  toolMessage: CoreToolMessage;
  messages: Array<Message>;
}): Array<Message> {
  return messages.map((message) => {
    if (message.toolInvocations) {
      return {
        ...message,
        toolInvocations: message.toolInvocations.map((toolInvocation) => {
          const toolResult = toolMessage.content.find(
            (tool) => tool.toolCallId === toolInvocation.toolCallId,
          );

          if (toolResult) {
            return {
              ...toolInvocation,
              state: 'result',
              result: toolResult.result,
            };
          }

          return toolInvocation;
        }),
      };
    }

    return message;
  });
}

export function convertToUIMessages(
  messages: Array<DBMessage>,
): Array<UIMessage> {
  return messages.reduce((chatMessages: Array<Message>, message) => {
    if (message.role === 'tool') {
      return addToolMessageToChat({
        toolMessage: message as CoreToolMessage,
        messages: chatMessages,
      });
    }

    let textContent = '';
    let reasoning: string | undefined = undefined;
    const toolInvocations: Array<ToolInvocation> = [];

    if (typeof message.content === 'string') {
      textContent = message.content;
    } else if (Array.isArray(message.content)) {
      for (const content of message.content) {
        if (content.type === 'text') {
          textContent += content.text;
        } else if (content.type === 'tool-call') {
          toolInvocations.push({
            state: 'call',
            toolCallId: content.toolCallId,
            toolName: content.toolName,
            args: content.args,
          });
        } else if (content.type === 'reasoning') {
          reasoning = content.reasoning;
        }
      }
    }

    chatMessages.push({
      id: message.id,
      role: message.role as Message['role'],
      content: textContent,
      // data: message.data,
      // data: message.data as JSONValue, // 如果确定数据是合法JSON结构
      // data: isJSONValue(message.data) ? message.data : undefined,
      
      // @ts-ignore
      data: message.data,

      // annotations: message.annotations,
      reasoning,
      toolInvocations,
    });

    return chatMessages;
  }, []);
}


export function convertToUIMessages_new(
  messages: ReadonlyArray<DBMessage>,
): UIMessage[] {
  const out: UIMessage[] = new Array(messages.length); // 预分配，避免扩容
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m.role === 'tool') {
      // 复用原逻辑，但直接改写下标，避免返回新数组
      addToolMessageToChat({ toolMessage: m as CoreToolMessage, messages: out });
      continue;
    }

    let text = '';
    let reasoning: string | undefined;
    // 预分配 4 个槽，80 % 场景不会超
    const tools: ToolInvocation[] = new Array(4);
    let toolCnt = 0;

    if (typeof m.content === 'string') {
      text = m.content;
    } else if (Array.isArray(m.content)) {
      for (let j = 0; j < m.content.length; j++) {
        const c = m.content[j];
        switch (c.type) {
          case 'text':
            text += c.text;
            break;
          case 'tool-call':
            tools[toolCnt++] = {
              state: 'call',
              toolCallId: c.toolCallId,
              toolName: c.toolName,
              args: c.args,
            };
            break;
          case 'reasoning':
            reasoning = c.reasoning;
            break;
        }
      }
    }

    out[i] = {
      id: m.id,
      index: i,
      role: m.role as Message['role'],
      content: text,
      // @ts-ignore
      data: (m?.data ?? {}) as JSONValue,
      reasoning,
      toolInvocations: toolCnt === tools.length ? tools : tools.slice(0, toolCnt),
    };
  }
  return out;
}

// 类型校验工具函数（需自行实现）
function isJSONValue(value: unknown): value is JSONValue {
  if (value === null) return true;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.every(isJSONValue);
  if (typeof value === 'object') {
    return Object.values(value as object).every(isJSONValue);
  }
  return false;
}

export type ResponseMessageWithoutId = CoreToolMessage | CoreAssistantMessage;
export type ResponseMessage = ResponseMessageWithoutId & { id?: string };

export function sanitizeResponseMessages({
  messages,
  reasoning,
}: {
  messages: Array<ResponseMessage>;
  reasoning: string | undefined;
}) {
  const toolResultIds: Array<string> = [];

  for (const message of messages) {
    if (message.role === 'tool') {
      for (const content of message.content) {
        if (content.type === 'tool-result') {
          toolResultIds.push(content.toolCallId);
        }
      }
    }
  }

  const messagesBySanitizedContent = messages.map((message) => {
    if (message.role !== 'assistant') return message;

    if (typeof message.content === 'string') return message;

    const sanitizedContent = message.content.filter((content) =>
      content.type === 'tool-call'
        ? toolResultIds.includes(content.toolCallId)
        : content.type === 'text'
          ? content.text.length > 0
          : true,
    );

    if (reasoning) {
      // @ts-expect-error: reasoning message parts in sdk is wip
      sanitizedContent.push({ type: 'reasoning', reasoning });
    }

    return {
      ...message,
      content: sanitizedContent,
    };
  });

  return messagesBySanitizedContent.filter(
    (message) => message.content.length > 0,
  );
}


// export function sanitizeResponseMessages(
//   messages: Array<CoreToolMessage | CoreAssistantMessage>,
// ): Array<CoreToolMessage | CoreAssistantMessage> {
//   const toolResultIds: Array<string> = [];

//   for (const message of messages) {
//     if (message.role === 'tool') {
//       for (const content of message.content) {
//         if (content.type === 'tool-result') {
//           toolResultIds.push(content.toolCallId);
//         }
//       }
//     }
//   }

//   const messagesBySanitizedContent = messages.map((message) => {
//     if (message.role !== 'assistant') return message;

//     if (typeof message.content === 'string') return message;

//     const sanitizedContent = message.content.filter((content) =>
//       content.type === 'tool-call'
//         ? toolResultIds.includes(content.toolCallId)
//         : content.type === 'text'
//           ? content.text.length > 0
//           : true,
//     );

//     return {
//       ...message,
//       content: sanitizedContent,
//     };
//   });

//   return messagesBySanitizedContent.filter(
//     (message) => message.content.length > 0,
//   );
// }

// export function sanitizeUIMessages(messages: Array<Message>): Array<Message> {
//   const messagesBySanitizedToolInvocations = messages.map((message) => {
//     if (message.role !== 'assistant') return message;

//     if (!message.toolInvocations) return message;

//     const toolResultIds: Array<string> = [];

//     for (const toolInvocation of message.toolInvocations) {
//       if (toolInvocation.state === 'result') {
//         toolResultIds.push(toolInvocation.toolCallId);
//       }
//     }

//     const sanitizedToolInvocations = message.toolInvocations.filter(
//       (toolInvocation) =>
//         toolInvocation.state === 'result' ||
//         toolResultIds.includes(toolInvocation.toolCallId),
//     );

//     return {
//       ...message,
//       toolInvocations: sanitizedToolInvocations,
//     };
//   });

//   return messagesBySanitizedToolInvocations.filter(
//     (message) =>
//       message.content.length > 0 ||
//       (message.toolInvocations && message.toolInvocations.length > 0),
//   );
// }


export function sanitizeUIMessages(messages: Array<Message>): Array<Message> {
  const messagesBySanitizedToolInvocations = messages.map((message) => {
    if (message.role !== 'assistant') return message;

    if (!message.toolInvocations) return message;

    const toolResultIds: Array<string> = [];

    for (const toolInvocation of message.toolInvocations) {
      if (toolInvocation.state === 'result') {
        toolResultIds.push(toolInvocation.toolCallId);
      }
    }

    const sanitizedToolInvocations = message.toolInvocations.filter(
      (toolInvocation) =>
        toolInvocation.state === 'result' ||
        toolResultIds.includes(toolInvocation.toolCallId),
    );

    return {
      ...message,
      toolInvocations: sanitizedToolInvocations,
    };
  });

  return messagesBySanitizedToolInvocations.filter(
    (message) =>
      message.content.length > 0 ||
      (message.toolInvocations && message.toolInvocations.length > 0),
  );
}


export function getMostRecentUserMessage(messages: Array<CoreMessage | Message>) {
  if (!Array.isArray(messages))
    return messages;
  if (messages.length === 1) 
    return messages.at(-1);
  const userMessages = messages.filter((message) => message.role === 'user');
  return userMessages.length > 0 ? userMessages.at(-1) : undefined;
}

export function getMostRecentUserMessages(messages: Array<CoreMessage | Message>) {
  // 定义 messages 数组
  // 从结尾向前遍历，获取最后连续出现的 content 为 'temp' 的 messages
  const userMessages: Array<CoreMessage | Message> = [];
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'user') {
      userMessages.unshift(messages[i]); // 添加到结果数组的开头
    } else {
      break; // 遇到非 'temp' 内容时停止遍历
    }
  }

  return userMessages;
}

export function getMostRecentMessage(messages: Array<CoreMessage | Message>) {
  return messages.length > 0 ? messages.at(-1) : undefined;
}

export function getMostRecentMessageRole(messages: Array<CoreMessage | Message>) {
  return messages.length > 0 ? messages.at(-1)?.role : '';
}

export function getDocumentTimestampByIndex(
  documents: Array<Document>,
  index: number,
) {
  if (!documents) return new Date();
  if (index > documents.length) return new Date();

  return documents[index].createdAt;
}

export function getMessageIdFromAnnotations(message: Message) {
  if (!message.annotations) return message.id;

  const [annotation] = message.annotations;
  if (!annotation) return message.id;

  // @ts-expect-error messageIdFromServer is not defined in MessageAnnotation
  return annotation.messageIdFromServer;
}

/** ***************************************** client utils *************************************** */


const points: Point[] = [
  { x: 10, y: 10 },
  { x: -10, y: 10 },
  { x: 10, y: -10 },
  { x: -10, y: -10 },
  { x: -10, y: -20 },
  { x: 10, y: -20 },
  { x: -10, y: 20 },
  { x: 10, y: 20 },
  { x: -20, y: 10 },
  { x: 20, y: 10 },
  { x: -20, y: -10 },
  { x: 20, y: -10 },
];

export function getRandomPoint(): Point {
  const randomIndex = Math.floor(Math.random() * points.length);
  return points[randomIndex];
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return func(...args);
  };
}

// 自行实现节流函数版本
export const throttle1 = <T extends (...args: any[]) => void>(fn: T, delay: number) => {
  let lastCall = 0;
  let pendingArgs: any[] | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    pendingArgs = args;

    if (now - lastCall < delay) {
      if (!timeoutId) {
        timeoutId = setTimeout(() => {
          // biome-ignore lint/style/noNonNullAssertion: <explanation>
          fn(...pendingArgs!);
          lastCall = Date.now();
          timeoutId = null;
          pendingArgs = null;
        }, delay - (now - lastCall));
      }
      return;
    }

    fn(...args);
    lastCall = now;
  };
};



export function getRectWidthByLength(len: number): number {
  // console.log(`limk.log.getRectWidthByLength len:`, len);
  let width = 130;
  if (len > 300) width = 500;
  if (len > 250) width = 400;
  if (len > 200) width = 370;
  if (len > 150) width = 360;
  if (len > 120) width = 350;
  if (len > 110) width = 330;
  if (len > 100) width = 300;
  if (len > 90) width = 290- 40;
  if (len > 80) width = 280- 40;
  if (len > 70) width = 270- 40;
  if (len > 60) width = 260- 30;
  if (len > 50) width = 250- 30;
  if (len > 45) width = 235- 30;
  if (len > 40) width = 220- 20;
  if (len > 35) width = 210- 20;  
  if (len > 30) width = 200- 20;  
  if (len > 25) width = 180- 20;
  if (len > 20) width = 160- 20;
  return width + 430;
}




export interface Position {
  start: {
      line: number;
      column: number;
      offset: number;
  };
  end: {
      line: number;
      column: number;
      offset: number;
  };
}

export function sumPositionFields(position: Position): string {
  const startLine = position.start.line || 0;
  const startColumn = position.start.column || 0;
  const startOffset = position.start.offset || 0;

  const endLine = position.end.line || 0;
  const endColumn = position.end.column || 0;
  const endOffset = position.end.offset || 0;

  const totalSum = startLine + startColumn + startOffset + endLine + endColumn + endOffset;

  return totalSum.toString();
}

type JSONValue = string | number | boolean | null | undefined | JSONValue[] | { [key: string]: JSONValue };

export function hasJASONData(data: JSONValue|undefined): boolean {
  // 检查 null 或 undefined
  if (data === null || data === undefined) {
    return false;
  }

  // 检查空对象
  if (typeof data === 'object' && Object.keys(data).length === 0) {
    return false;
  }

  // 检查空数组
  if (Array.isArray(data) && data.length === 0) {
    return false;
  }

  // 其他情况，表示有数据
  return true;
}
          
// 动态延迟调整函数
let throttleDelay = 300;

const dynamicThrottle = (event: any) => {
  const deltaLength = event.chunk.textDelta?.length || 0;
  
  if (deltaLength > 500) {
    throttleDelay = Math.min(300, throttleDelay + 100);
  } else if (deltaLength > 0) {
    throttleDelay = Math.max(200, throttleDelay - 50);
  }
};


// 节流函数实现（支持动态延迟）
export   const createDynamicThrottle = () => {
  let lastCall = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let pendingArgs: any[] | null = null;
  
  return (event: any, callback: (event: any) => void) => {
    // 动态调整延迟（先执行延迟计算）
    dynamicThrottle(event);
    
    const now = Date.now();
    const elapsed = now - lastCall;
    
    // 立即执行条件
    if (elapsed >= throttleDelay) {
      lastCall = now;
      callback(event);
      return;
    }

    // 存储最新参数
    pendingArgs = [event];
    
    // 清除旧定时器
    if (timeoutId) clearTimeout(timeoutId);
    
    // 设置新定时器（使用最新延迟）
    timeoutId = setTimeout(() => {
      if (pendingArgs) {
        callback(pendingArgs[0]);
        lastCall = Date.now();
        pendingArgs = null;
      }
    }, throttleDelay - elapsed);
  };
};

// 创建智能批量处理器
export const createBatchedSender = () => {
  let buffer: string[] = []; // 存储 reasoning 的 textDelta
  let timer: NodeJS.Timeout | null = null;
  let lastType: string | null = null; // 跟踪上一个事件类型

  // 动态节流参数
  let throttleDelay = 50;
  const MIN_DELAY = 20;
  const MAX_DELAY = 200;

  return (event:any, send: (e: any) => void) => {
    // 动态调整节流延迟
    const deltaLength = event.chunk.type === 'reasoning' 
      ? event.chunk.textDelta.length
      : 0;
    
    throttleDelay = Math.min(MAX_DELAY, 
      Math.max(MIN_DELAY, 
        throttleDelay + (deltaLength > 100 ? 10 : -5)
      )
    );

    // 处理 reasoning 类型
    if (event.chunk.type === 'reasoning') {
      buffer.push(event.chunk.textDelta);
      
      // 如果前一个事件不是 reasoning，立即发送合并结果
      if (lastType !== 'reasoning' && buffer.length > 0) {
        flushBuffer(send);
      } else {
        // 启动/重置节流定时器
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => flushBuffer(send), throttleDelay);
      }
    } else {
      // 非 reasoning 事件立即发送，并触发缓存清理
      if (buffer.length > 0) flushBuffer(send);
      send(event);
    }

    lastType = event.chunk.type;
  };

  function flushBuffer(send: (e: any) => void) {
    if (buffer.length === 0) return;

    // 构造合并后的 reasoning 事件
    const mergedEvent = {
      ...event,
      chunk: {
        type: 'reasoning',
        textDelta: buffer.join('')
      }
    };

    send(mergedEvent);
    buffer = [];
    if (timer) clearTimeout(timer);
  }
};


// export function convertCoreToOpenAIMessage(coreMsg: CoreMessage): ChatCompletionMessageParam {
//   if (coreMsg.role === 'user') {
//     // 转换用户消息内容
//     const content = coreMsg.content
//       .map(part => {
//         if (part.type === 'text') {
//           return { type: 'text', text: part.text } satisfies ChatCompletionContentPartText;
//         }
//         if (part.type === 'image') {
//           // 假设 ImagePart 需要转换为 OpenAI 的图片格式
//           return {
//             type: 'image_url',
//             image_url: { 
//               url: part.imageUrl,
//               detail: part.detail || 'auto'
//             }
//           } satisfies ChatCompletionContentPartImage;
//         }
//         // 过滤掉不支持的文件类型
//         return null;
//       })
//       .filter(Boolean) as ChatCompletionContentPart[];

//     return {
//       role: 'user',
//       content: content.length > 0 ? content : '空消息'
//     };
//   }
  
//   // 其他角色消息保持不变
//   return coreMsg as unknown as ChatCompletionMessageParam;
// }

